// useProjectTaskGroups.js
// Shared project task groups — visible to ALL authenticated users in real time.
//
// How cross-user sharing works (three complementary layers):
//
//   1. Supabase Broadcast  (always works, no DB tables needed)
//      • When any user changes groups/assignments the full state is broadcast
//        on channel `bcast-ptg-{projectId}`.
//      • All other users subscribed to that channel receive the update
//        immediately (~100 ms) and update their local state.
//      • Also fires a "request state" event when joining so that any user
//        already on the project can send the current state to the newcomer.
//
//   2. app_settings table  (cross-user persistence, works on every Supabase
//      project that allows authenticated writes to app_settings)
//      • Groups stored under key `ptg_{projectId}`, map under `ptgm_{projectId}`
//      • Used as the read source on page load so late-joining users see
//        the groups even when no one else is currently online.
//
//   3. Dedicated tables  (best — used when migration has been applied)
//      • project_task_groups + project_task_group_map
//      • Also has Supabase Realtime postgres_changes subscriptions.
//
//   4. localStorage  (offline cache / instant paint on first render only)
//
// Follows the same patterns as useProjectWeeklyTasks.js:
//   • snake_case in DB → camelCase in UI
//   • requireSession() before mutations
//   • { success, data?, error? } return shape

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { requireSession } from '../lib/supabaseAuthHelpers';

// Debounce helper
function makeDebounced(fn, delay) {
  var timer = null;
  return function () { clearTimeout(timer); timer = setTimeout(fn, delay); };
}

const DEBUG = process.env.NODE_ENV !== 'production';
function dbg(...a) { if (DEBUG) console.log('[PTGroups]', ...a); }
function dbgErr(...a) { if (DEBUG) console.error('[PTGroups]', ...a); }

// ── Helpers ────────────────────────────────────────────────────────────────

function isMissingTable(error) {
  if (!error) return false;
  var msg = String(error.message || error.code || error);
  // Only match the Postgres "relation does not exist" error (42P01) or its text form.
  // Do NOT match generic "not found" — that catches HTTP 404 network errors and would
  // incorrectly set dbAvailable=false for the whole session, bypassing all DB writes.
  return msg.includes('42P01') || msg.toLowerCase().includes('does not exist');
}

function normalizeGroup(row) {
  return {
    id: String(row.id),
    projectId: row.project_id,
    name: row.name,
    color: row.color || null,
    order: row.position != null ? row.position : (row.order != null ? row.order : 0),
  };
}

// ── localStorage (instant first-paint cache) ─────────────────────────────
function lsKey(prefix, pid) { return prefix + '_' + pid; }
function lsLoad(pid) {
  try {
    var sg = localStorage.getItem(lsKey('brio_taskGroups', pid));
    var sm = localStorage.getItem(lsKey('brio_taskGroupMap', pid));
    return { groups: sg ? JSON.parse(sg) : [], taskGroupMap: sm ? JSON.parse(sm) : {} };
  } catch (_) { return { groups: [], taskGroupMap: {} }; }
}
function lsSaveGroups(pid, groups) {
  try { localStorage.setItem(lsKey('brio_taskGroups', pid), JSON.stringify(groups)); } catch (_) {}
}
function lsSaveMap(pid, map) {
  try { localStorage.setItem(lsKey('brio_taskGroupMap', pid), JSON.stringify(map)); } catch (_) {}
}

// ── app_settings (cross-user persistence fallback) ────────────────────────
function asGroupKey(pid) { return 'ptg_' + pid; }
function asMapKey(pid)   { return 'ptgm_' + pid; }

async function asLoad(pid) {
  try {
    var res = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', [asGroupKey(pid), asMapKey(pid)]);
    if (res.error) { dbgErr('asLoad error', res.error); return null; }
    var grpRow = (res.data || []).find(function (r) { return r.key === asGroupKey(pid); });
    var mapRow = (res.data || []).find(function (r) { return r.key === asMapKey(pid); });
    return {
      groups: grpRow && Array.isArray(grpRow.value) ? grpRow.value : [],
      taskGroupMap: mapRow && mapRow.value && typeof mapRow.value === 'object' && !Array.isArray(mapRow.value) ? mapRow.value : {},
    };
  } catch (e) { dbgErr('asLoad exception', e); return null; }
}

async function asSaveGroups(pid, groups) {
  try {
    var res = await supabase
      .from('app_settings')
      .upsert({ key: asGroupKey(pid), value: groups, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (res.error) dbgErr('asSaveGroups error', res.error);
  } catch (e) { dbgErr('asSaveGroups exception', e); }
}

async function asSaveMap(pid, map) {
  try {
    var res = await supabase
      .from('app_settings')
      .upsert({ key: asMapKey(pid), value: map, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (res.error) dbgErr('asSaveMap error', res.error);
  } catch (e) { dbgErr('asSaveMap exception', e); }
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useProjectTaskGroups(projectId) {
  var [groups, setGroupsState] = useState([]);
  var [taskGroupMap, setTaskGroupMapState] = useState({});
  var [loading, setLoading] = useState(false);
  var [dbAvailable, _setDbAvailable] = useState(null); // null=unknown true=tables false=fallback
  var [mapHasProjectId, setMapHasProjectId] = useState(null);

  var projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  // Refs that mirror state synchronously — used inside state-setter callbacks
  // and for broadcast payloads without stale-closure issues.
  var groupsRef = useRef([]);
  var mapRef    = useRef({});

  // Ref-synced dbAvailable so wrappers can read it without re-creating
  var dbAvailableRef = useRef(null);

  // Set to true the first time the DB returns at least one group for this project.
  // Once true, an empty DB result is treated as "all groups intentionally deleted"
  // rather than "auth race / pre-migration" — preventing stale localStorage from
  // overwriting or re-inserting groups that the user just deleted.
  var hasSeenDBGroupsRef = useRef(false);

  // True once load() has committed this project's assignments from a durable
  // source. Until then mapRef only holds this device's localStorage seed, which
  // may be empty (new browser) or stale, so it must not be handed to peers.
  var mapLoadedRef = useRef(false);
  var setDbAvailable = useCallback(function (val) {
    dbAvailableRef.current = val;
    _setDbAvailable(val);
  }, []);

  // Broadcast channel ref — set when subscribed
  var broadcastRef = useRef(null);

  // ── Broadcast helpers ─────────────────────────────────────────────────
  // Send the full group state to all peers on this project.
  function sendBroadcast(grps, mp) {
    var ch = broadcastRef.current;
    if (!ch) return;
    var pid = projectIdRef.current;
    if (!pid) return;
    ch.send({
      type: 'broadcast',
      event: 'grp',
      payload: { groups: grps || [], map: mp || {}, pid: String(pid) },
    }).catch(function () {}); // fire-and-forget
  }

  // ── Wrapped setters ────────────────────────────────────────────────────
  // Keep groupsRef/mapRef in sync (synchronously in the updater), persist
  // to localStorage, and write to app_settings when using fallback mode.
  var setGroups = useCallback(function (updater) {
    setGroupsState(function (prev) {
      var next = typeof updater === 'function' ? updater(prev) : updater;
      groupsRef.current = next;
      var pid = projectIdRef.current;
      if (pid) {
        lsSaveGroups(pid, next);
        if (dbAvailableRef.current !== true) asSaveGroups(pid, next);
      }
      return next;
    });
  }, []);

  var setTaskGroupMap = useCallback(function (updater) {
    setTaskGroupMapState(function (prev) {
      var next = typeof updater === 'function' ? updater(prev) : updater;
      mapRef.current = next;
      var pid = projectIdRef.current;
      if (pid) {
        lsSaveMap(pid, next);
        // Always mirror task→group assignments to app_settings, even when the
        // dedicated project_task_group_map table is present (dbAvailable === true).
        // That table can never actually store these rows: the grouped tasks come
        // from project_weekly_tasks (UUID ids), but project_task_group_map.task_id
        // is bigint, so the write path's Number(taskId) evaluates to NaN and every
        // dedicated-table upsert fails silently — which is why that table has 0
        // rows across every project. Without this mirror the assignments live only
        // in the ephemeral broadcast channel + this device's localStorage: visible
        // to peers online at the same time, but lost on any other device or later
        // session. The app_settings mirror is the durable, cross-user store that
        // load() reads back (its JSON keys hold the UUIDs verbatim, no coercion).
        asSaveMap(pid, next);
      }
      return next;
    });
  }, []);

  // ── Load ──────────────────────────────────────────────────────────────
  var load = useCallback(async function (pid, isRetry) {
    if (!pid) {
      setGroupsState([]); groupsRef.current = [];
      setTaskGroupMapState({}); mapRef.current = {};
      return;
    }

    // Paint from localStorage immediately for a fast first render.
    // Only update the React state (visual), NOT the refs — the refs hold authoritative
    // state (from DB or user mutations) so that broadcast responses and sendBroadcast()
    // calls never propagate stale localStorage data to other users.
    var cached = lsLoad(pid);
    setGroupsState(cached.groups);
    setTaskGroupMapState(cached.taskGroupMap);

    // Ensure a valid session before making RLS-protected queries.
    // Without this, the Supabase client may send the first request before the
    // JWT is attached (brief async window on page load), causing the RLS policy
    // "TO authenticated" to silently return [] with no error — leaving state empty.
    try { await requireSession(); } catch (_) { return; }

    setLoading(true);
    try {
      // ── 1. Try dedicated tables ──────────────────────────────────────
      var grpRes = await supabase
        .from('project_task_groups')
        .select('id, project_id, name, color, position')
        .eq('project_id', pid)
        .order('position', { ascending: true });

      if (grpRes.error) {
        if (isMissingTable(grpRes.error)) {
          dbg('project_task_groups table missing — trying app_settings fallback');
          setDbAvailable(false);

          // ── 1b. app_settings fallback ────────────────────────────────
          var asFallback = await asLoad(pid);
          if (projectIdRef.current !== pid) return;

          if (asFallback) {
            var fg = asFallback.groups.length > 0 ? asFallback.groups : cached.groups;
            var fm = Object.keys(asFallback.taskGroupMap).length > 0 ? asFallback.taskGroupMap : cached.taskGroupMap;
            setGroupsState(fg);   groupsRef.current = fg;
            setTaskGroupMapState(fm); mapRef.current = fm; mapLoadedRef.current = true;
            lsSaveGroups(pid, fg);
            lsSaveMap(pid, fm);
            // Backfill: if localStorage had data but app_settings didn't, write it now
            if (asFallback.groups.length === 0 && cached.groups.length > 0) {
              dbg('backfilling app_settings from localStorage');
              asSaveGroups(pid, cached.groups);
            }
            if (Object.keys(asFallback.taskGroupMap).length === 0 && Object.keys(cached.taskGroupMap).length > 0) {
              asSaveMap(pid, cached.taskGroupMap);
            }
            dbg('loaded', fg.length, 'groups from app_settings for project', pid);
          }
        } else {
          dbgErr('load: group query error', grpRes.error);
        }
        return;
      }

      setDbAvailable(true);
      var normalizedGroups = (grpRes.data || []).map(normalizeGroup);

      // ── 2. Load assignments ──────────────────────────────────────────
      var map = {};
      var mapDbOk = true;

      var mapRes = await supabase
        .from('project_task_group_map')
        .select('task_id, group_id')
        .eq('project_id', pid);

      if (mapRes.error) {
        if (isMissingTable(mapRes.error)) {
          mapDbOk = false; setMapHasProjectId(false);
        } else if (mapRes.error.message && mapRes.error.message.toLowerCase().includes('project_id')) {
          setMapHasProjectId(false);
          if (normalizedGroups.length > 0) {
            var gIds = normalizedGroups.map(function (g) { return Number(g.id); });
            var mbRes = await supabase.from('project_task_group_map').select('task_id, group_id').in('group_id', gIds);
            if (!mbRes.error) (mbRes.data || []).forEach(function (r) { map[String(r.task_id)] = String(r.group_id); });
            else mapDbOk = false;
          }
        } else {
          dbgErr('load: map query error', mapRes.error); mapDbOk = false;
        }
      } else {
        setMapHasProjectId(true);
        (mapRes.data || []).forEach(function (r) { map[String(r.task_id)] = String(r.group_id); });
        // Strategy B fallback for NULL project_id rows (read-only — no DB write).
        // These rows were written before migration 20260330 added the project_id column.
        // We query by group_id instead so we can still display assignments correctly.
        // We intentionally do NOT upsert project_id back: that upsert would trigger a
        // Supabase Realtime event for every user on this project, causing cascading
        // debouncedReload calls that can overwrite each other's state (ping-pong).
        // The project_id column is populated naturally as users move tasks via
        // moveTaskToGroup(), which always writes project_id. Over time all rows gain it.
        if (Object.keys(map).length === 0 && normalizedGroups.length > 0) {
          var gIdsB = normalizedGroups.map(function (g) { return Number(g.id); });
          var mbResB = await supabase.from('project_task_group_map').select('task_id, group_id').in('group_id', gIdsB);
          if (!mbResB.error && mbResB.data && mbResB.data.length > 0) {
            mbResB.data.forEach(function (r) { map[String(r.task_id)] = String(r.group_id); });
          }
        }
      }

      if (projectIdRef.current !== pid) return;

      // ── 3. Commit to state ───────────────────────────────────────────
      if (normalizedGroups.length > 0) hasSeenDBGroupsRef.current = true;
      // When the DB returns groups, use them authoritatively.
      // When the DB returns zero groups, fall back to the localStorage cache.
      // This handles the case where the Supabase WebSocket briefly reconnects
      // after laptop sleep and the first query returns empty before auth is
      // fully re-established. The cache reflects the last known good state:
      // deleteGroup() always writes the post-deletion (empty or reduced) groups
      // to localStorage before the DB delete, so stale groups are never
      // resurrected — the cache IS the correct empty state after a real deletion.
      var finalGroups = normalizedGroups.length > 0 ? normalizedGroups : cached.groups;
      setGroupsState(finalGroups); groupsRef.current = finalGroups;
      if (normalizedGroups.length > 0) lsSaveGroups(pid, normalizedGroups);

      // ── 3a. Backfill localStorage → DB (one-time migration) ──────────
      // When tables were just applied the DB is empty but the user already
      // has groups in localStorage (created before migrations existed).
      // Insert them now so other users can see them on their next load.
      // Only backfill when we have NEVER seen groups in the DB for this project
      // (i.e. tables were just migrated and old localStorage data predates them).
      // When hasSeenDBGroupsRef is already true the empty DB result means the
      // user intentionally deleted all groups — do NOT re-insert stale cache data.
      if (normalizedGroups.length === 0 && cached.groups.length > 0 && !hasSeenDBGroupsRef.current) {
        dbg('DB empty but localStorage has', cached.groups.length, 'group(s) — backfilling to dedicated tables');
        var lsGroups = cached.groups;
        var lsMap    = cached.taskGroupMap || {};
        requireSession().then(async function () {
          try {
            var idRemap = {}; // tempId (grp_xxx) → real DB id

            // Insert each group; groups with temp IDs get a new DB-assigned id.
            for (var gi = 0; gi < lsGroups.length; gi++) {
              var lg = lsGroups[gi];
              var isTemp = String(lg.id).startsWith('grp_');
              if (isTemp) {
                var ins = await supabase
                  .from('project_task_groups')
                  .insert({ project_id: Number(pid), name: lg.name, color: lg.color || null, position: lg.order || gi })
                  .select()
                  .single();
                if (ins.error) { dbgErr('backfill insert group error', ins.error); continue; }
                idRemap[lg.id] = String(ins.data.id);
                dbg('backfilled', lg.id, '→', ins.data.id);
              } else {
                // Group already had a real id — upsert in case it was lost
                await supabase
                  .from('project_task_groups')
                  .upsert({ id: Number(lg.id), project_id: Number(pid), name: lg.name, color: lg.color || null, position: lg.order || gi }, { onConflict: 'id' });
              }
            }

            // Insert task assignments, remapping any temp group ids
            var mapPayloads = Object.keys(lsMap).map(function (tid) {
              var gid = lsMap[tid];
              var realGid = idRemap[gid] !== undefined ? idRemap[gid] : gid;
              return isNaN(Number(realGid)) ? null : { task_id: Number(tid), group_id: Number(realGid), project_id: Number(pid) };
            }).filter(Boolean);

            if (mapPayloads.length > 0) {
              await supabase.from('project_task_group_map').upsert(mapPayloads, { onConflict: 'task_id' });
              dbg('backfilled', mapPayloads.length, 'task assignment(s)');
            }

            // Update localStorage so temp ids are replaced by real ids
            if (Object.keys(idRemap).length > 0 && projectIdRef.current === pid) {
              var remappedGroups = lsGroups.map(function (g) {
                return idRemap[g.id] ? { ...g, id: idRemap[g.id] } : g;
              });
              var remappedMap = {};
              Object.keys(lsMap).forEach(function (tid) {
                var gid = lsMap[tid];
                remappedMap[tid] = idRemap[gid] !== undefined ? idRemap[gid] : gid;
              });
              lsSaveGroups(pid, remappedGroups);
              lsSaveMap(pid, remappedMap);
            }

            // Reload from DB so the authoritative data (real ids) is in state
            if (projectIdRef.current === pid) load(pid);
          } catch (e) { dbgErr('backfill error', e); }
        }).catch(function (e) { dbgErr('backfill session error', e); });
      }

      if (mapDbOk) {
        var finalMap = {};
        if (Object.keys(map).length > 0) {
          finalMap = map;
        } else {
          // The dedicated project_task_group_map table returned no rows (it is in
          // fact always empty — see the task_id type-mismatch note in
          // setTaskGroupMap). Recover the assignments from the durable app_settings
          // mirror that setTaskGroupMap writes on every mutation. This is what makes the
          // grouping survive across devices and across sessions that are not
          // online at the same time: the ephemeral broadcast channel keeps
          // concurrent peers in sync but persists nothing, so without this read a
          // user opening the page on another machine would see every task
          // ungrouped even though the group tabs (loaded from project_task_groups)
          // are present.
          var asMap = null;
          try {
            var asSnap = await asLoad(pid);
            if (asSnap && asSnap.taskGroupMap && Object.keys(asSnap.taskGroupMap).length > 0) {
              asMap = asSnap.taskGroupMap;
            }
          } catch (_) { asMap = null; }
          if (projectIdRef.current !== pid) return;

          // Prefer the cross-user app_settings mirror; fall back to this device's
          // localStorage cache for display only.
          var recoveredSource = asMap ||
            (cached.taskGroupMap && Object.keys(cached.taskGroupMap).length > 0 ? cached.taskGroupMap : null);
          if (recoveredSource) {
            // Keep only assignments whose group still exists in the loaded set so a
            // stale reference to a deleted group never resurrects it.
            var validGroupIds = {};
            finalGroups.forEach(function (g) { validGroupIds[String(g.id)] = true; });
            Object.keys(recoveredSource).forEach(function (tid) {
              var gid = String(recoveredSource[tid]);
              if (validGroupIds[gid]) finalMap[tid] = gid;
            });
            // If the recovery came from this device's localStorage but the shared
            // mirror was empty, seed the mirror so other devices can see these
            // assignments too. Safe: the mirror is empty here, so this only adds
            // data and can never clobber a populated mirror.
            if (!asMap && Object.keys(finalMap).length > 0) asSaveMap(pid, finalMap);
          }
        }
        setTaskGroupMapState(finalMap); mapRef.current = finalMap; mapLoadedRef.current = true;
        // Persist to localStorage whenever we resolved real assignments from any
        // durable source (dedicated table or app_settings mirror). Never write an
        // empty map over a populated cache — that would corrupt first paint and
        // broadcast {} to peers.
        if (Object.keys(finalMap).length > 0) lsSaveMap(pid, finalMap);
      }

      dbg('loaded', finalGroups.length, 'groups from dedicated tables for project', pid);

      // If the DB responded successfully but returned zero groups, schedule a
      // single retry after 1.5 s.  This handles two cases:
      //   1. Auth race: JWT not yet accepted by RLS on the very first request.
      //   2. Reconnect blip: WebSocket just reconnected (e.g. laptop wake), DB
      //      briefly returns empty before the connection is fully authenticated.
      // isRetry guard ensures this fires at most once — without it, projects with
      // no groups would keep retrying indefinitely.
      // If the retry also returns zero groups we accept that as authoritative
      // (project genuinely has no groups, or all groups were intentionally deleted).
      if (normalizedGroups.length === 0 && !isRetry && projectIdRef.current === pid) {
        setTimeout(function () {
          if (projectIdRef.current === pid) load(pid, true);
        }, 1500);
      }
    } catch (e) {
      dbgErr('load error', e);
    } finally {
      if (projectIdRef.current === pid) setLoading(false);
    }
  }, [setDbAvailable]);

  useEffect(function () {
    hasSeenDBGroupsRef.current = false; // fresh project → unknown DB state
    mapLoadedRef.current = false;
    // Seed refs immediately from the localStorage cache before load() starts.
    // Without this, groupsRef and mapRef are [] / {} from the time the broadcast
    // channel subscribes until the DB response arrives (~100-500 ms). Any group
    // mutation in that window (rename, reorder, create) would call sendBroadcast
    // with mapRef.current = {} — wiping all task assignments for every connected
    // peer. Seeding from cache fills the gap: the cache already reflects the
    // latest known state (deleteGroup and moveTaskToGroup both write to it
    // synchronously before touching the DB), so stale resurrection is not a risk.
    var seedCache = lsLoad(projectId || '');
    groupsRef.current = seedCache.groups;
    mapRef.current = seedCache.taskGroupMap;
    load(projectId);
  }, [projectId, load]);

  // Stable ref so the visibility listener always calls the latest load
  // without being included in effect deps.
  var loadStableRef = useRef(null);
  loadStableRef.current = load;

  // ── Refetch groups when tab becomes visible ────────────────────────────
  // Same rationale as in useProjectWeeklyTasks: stale tabs that wake from
  // sleep need fresh DB state before broadcasting group changes to peers.
  useEffect(function () {
    if (!projectId) return;
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      dbg('Tab became visible — reloading groups to clear stale state');
      if (loadStableRef.current) loadStableRef.current(projectId);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return function () {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [projectId]);

  // ── Broadcast channel ─────────────────────────────────────────────────
  // This is the PRIMARY real-time mechanism — works with zero DB migrations.
  // All users on the same project share state through the broadcast channel.
  useEffect(function () {
    if (!projectId) return;
    var pid = String(projectId);

    var ch = supabase.channel('bcast-ptg-' + pid, {
      config: { broadcast: { self: false } }, // don't echo own messages back
    });

    ch.on('broadcast', { event: 'grp' }, function (msg) {
      // Another user sent their full group state — adopt it.
      var payload = msg && msg.payload;
      if (!payload) return;
      if (String(payload.pid) !== pid) return; // wrong project

      var inGroups = Array.isArray(payload.groups) ? payload.groups : [];
      var inMap    = (payload.map && typeof payload.map === 'object') ? payload.map : {};

      // Only adopt if the sender has groups — never regress to empty.
      if (inGroups.length === 0) return;

      // An empty map is never adopted as is: a peer answering before its own
      // assignments loaded sends its groups with {} and wiped every task into
      // Ungrouped here. Keep our own assignments instead, dropping only those
      // whose group the peer no longer has (a deleted group).
      if (Object.keys(inMap).length === 0) {
        var keepIds = {};
        inGroups.forEach(function (g) { keepIds[String(g.id)] = true; });
        var kept = {};
        Object.keys(mapRef.current || {}).forEach(function (tid) {
          if (keepIds[String(mapRef.current[tid])]) kept[tid] = mapRef.current[tid];
        });
        inMap = kept;
      }

      dbg('broadcast received:', inGroups.length, 'groups from peer');
      setGroupsState(inGroups);    groupsRef.current = inGroups;
      setTaskGroupMapState(inMap); mapRef.current = inMap;
      lsSaveGroups(pid, inGroups);
      lsSaveMap(pid, inMap);
      // Received maps are not written to the app_settings mirror: the sender has
      // already saved its own change, and a peer's map may be its stale
      // localStorage seed, which would overwrite newer assignments for everyone.
    });

    ch.on('broadcast', { event: 'grp-req' }, function (msg) {
      // A peer joined and is requesting the current state.
      var payload = msg && msg.payload;
      if (!payload || String(payload.pid) !== pid) return;
      // Answer only once our own assignments have loaded; before that mapRef is
      // just this device's cache and could be empty or out of date.
      if (!mapLoadedRef.current) return;
      var g = groupsRef.current;
      var m = mapRef.current;
      if (g && g.length > 0) {
        dbg('peer requested state — sending', g.length, 'groups');
        ch.send({ type: 'broadcast', event: 'grp', payload: { groups: g, map: m, pid: pid } })
          .catch(function () {});
      }
    });

    ch.subscribe(function (status) {
      if (status !== 'SUBSCRIBED') return;
      broadcastRef.current = ch;
      dbg('broadcast channel subscribed for project', pid);

      // Request state from any peers already on this project.
      ch.send({ type: 'broadcast', event: 'grp-req', payload: { pid: pid } })
        .catch(function () {});
    });

    return function () {
      broadcastRef.current = null;
      supabase.removeChannel(ch);
    };
  }, [projectId]);

  // ── Postgres-changes realtime (dedicated tables + app_settings) ────────
  useEffect(function () {
    if (!projectId) return;
    var nid = Number(projectId);
    if (!nid) return;

    var debouncedReload = makeDebounced(function () { load(projectId); }, 400);

    var s1 = supabase.channel('ptg-rt-' + nid)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_task_groups', filter: 'project_id=eq.' + nid }, debouncedReload)
      .subscribe();
    // Note: filter requires the project_id column on project_task_group_map
    // (added in migration 20260330). Without the filter this fires for ALL
    // projects, causing spurious reloads. If the column is absent Supabase
    // Realtime will surface a subscription error in the console; the load()
    // called from the visibility handler still keeps state correct in that case.
    var s2 = supabase.channel('ptgm-rt-' + nid)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_task_group_map', filter: 'project_id=eq.' + nid }, debouncedReload)
      .subscribe();
    var s3 = supabase.channel('as-ptg-rt-' + nid)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings', filter: 'key=eq.' + asGroupKey(nid) }, debouncedReload)
      .subscribe();
    var s4 = supabase.channel('as-ptgm-rt-' + nid)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings', filter: 'key=eq.' + asMapKey(nid) }, debouncedReload)
      .subscribe();

    return function () {
      [s1, s2, s3, s4].forEach(function (s) { supabase.removeChannel(s); });
    };
  }, [projectId, load]);

  // ── Create group ───────────────────────────────────────────────────────
  var createGroup = useCallback(async function (name, color, position) {
    var tempId = 'grp_' + Date.now();
    var optimistic = { id: tempId, projectId: projectId, name: name || 'Untitled Group', color: color || null, order: position || 0 };
    // Compute new groups BEFORE calling setter so sendBroadcast sends the post-mutation state.
    // setGroups' updater runs inside setGroupsState (async) — groupsRef.current is NOT yet updated
    // when sendBroadcast runs if we read it after calling setGroups.
    var optimisticGroups = [...groupsRef.current, optimistic];
    setGroups(function () { return optimisticGroups; });
    // Broadcast optimistic state so peers see the new group immediately
    sendBroadcast(optimisticGroups, mapRef.current);

    if (dbAvailable === false) {
      // setGroups wrapper already wrote to app_settings
      return { success: true, data: optimistic };
    }

    try {
      await requireSession();
      var { data, error } = await supabase
        .from('project_task_groups')
        .insert({ project_id: projectId, name: name || 'Untitled Group', color: color || null, position: position || 0 })
        .select()
        .single();

      if (error) {
        if (isMissingTable(error)) {
          dbg('project_task_groups table missing — saved via app_settings + broadcast');
          setDbAvailable(false);
          return { success: true, data: optimistic };
        }
        dbgErr('createGroup DB error', error);
        throw error;
      }

      var real = normalizeGroup(data);
      // Compute confirmed state before calling setters (refs are stale until React renders).
      var confirmedGroups = groupsRef.current.map(function (g) { return g.id === tempId ? real : g; });
      var remappedTaskIds = [];
      var confirmedMap = { ...mapRef.current };
      Object.keys(confirmedMap).forEach(function (tid) {
        if (confirmedMap[tid] === tempId) { confirmedMap[tid] = real.id; remappedTaskIds.push(tid); }
      });
      setGroups(function () { return confirmedGroups; });
      setTaskGroupMap(function () { return confirmedMap; });
      if (remappedTaskIds.length > 0) {
        await Promise.all(remappedTaskIds.map(function (tid) {
          var p = { task_id: Number(tid), group_id: Number(real.id) };
          if (mapHasProjectId !== false) p.project_id = Number(projectId);
          return supabase.from('project_task_group_map').upsert(p, { onConflict: 'task_id' });
        })).catch(function (e) { dbgErr('createGroup remapped assignments error', e); });
      }
      setDbAvailable(true);
      sendBroadcast(confirmedGroups, confirmedMap);
      dbg('created group', real.id);
      return { success: true, data: real };
    } catch (e) {
      dbgErr('createGroup error', e);
      return { success: true, data: optimistic };
    }
  }, [projectId, dbAvailable, mapHasProjectId, setGroups, setTaskGroupMap, setDbAvailable]);

  // ── Rename group ───────────────────────────────────────────────────────
  var renameGroup = useCallback(async function (groupId, newName) {
    var trimmed = (newName || '').trim() || 'Untitled Group';
    var renamedGroups = groupsRef.current.map(function (g) { return g.id === groupId ? { ...g, name: trimmed } : g; });
    setGroups(function () { return renamedGroups; });
    sendBroadcast(renamedGroups, mapRef.current);
    if (dbAvailable === false || String(groupId).startsWith('grp_')) return { success: true };
    try {
      await requireSession();
      var { error } = await supabase.from('project_task_groups')
        .update({ name: trimmed, updated_at: new Date().toISOString() }).eq('id', groupId);
      if (error) throw error;
      return { success: true };
    } catch (e) { dbgErr('renameGroup error', e); return { success: false, error: e.message }; }
  }, [dbAvailable, setGroups]);

  // ── Delete group ───────────────────────────────────────────────────────
  var deleteGroup = useCallback(async function (groupId) {
    var deletedGroups = groupsRef.current.filter(function (g) { return g.id !== groupId; });
    var deletedMap = { ...mapRef.current };
    Object.keys(deletedMap).forEach(function (tid) { if (deletedMap[tid] === groupId) delete deletedMap[tid]; });
    // Update refs and localStorage synchronously BEFORE the React state update so
    // any load() triggered by Realtime sees the correct empty state immediately,
    // rather than the stale pre-deletion cache that may still be in localStorage if
    // React hasn't yet committed the setGroupsState updater callback.
    groupsRef.current = deletedGroups;
    mapRef.current = deletedMap;
    var _pid = projectIdRef.current;
    if (_pid) { lsSaveGroups(_pid, deletedGroups); lsSaveMap(_pid, deletedMap); }
    setGroups(function () { return deletedGroups; });
    setTaskGroupMap(function () { return deletedMap; });
    sendBroadcast(deletedGroups, deletedMap);
    if (dbAvailable === false || String(groupId).startsWith('grp_')) return { success: true };
    try {
      await requireSession();
      var { error } = await supabase.from('project_task_groups').delete().eq('id', groupId);
      if (error) throw error;
      return { success: true };
    } catch (e) { dbgErr('deleteGroup error', e); return { success: false, error: e.message }; }
  }, [dbAvailable, setGroups, setTaskGroupMap]);

  // ── Move task to group ─────────────────────────────────────────────────
  var moveTaskToGroup = useCallback(async function (taskId, groupId) {
    var tid = String(taskId);
    var movedMap = { ...mapRef.current };
    if (!groupId) { delete movedMap[tid]; } else { movedMap[tid] = String(groupId); }
    setTaskGroupMap(function () { return movedMap; });
    sendBroadcast(groupsRef.current, movedMap);
    if (dbAvailable === false || (groupId && String(groupId).startsWith('grp_'))) return { success: true };
    try {
      await requireSession();
      if (!groupId) {
        var { error: delErr } = await supabase.from('project_task_group_map').delete().eq('task_id', Number(taskId));
        if (delErr) throw delErr;
      } else {
        var payload = { task_id: Number(taskId), group_id: Number(groupId) };
        if (mapHasProjectId !== false) payload.project_id = Number(projectId);
        var { error: upsErr } = await supabase.from('project_task_group_map').upsert(payload, { onConflict: 'task_id' });
        if (upsErr) {
          if (upsErr.message && upsErr.message.toLowerCase().includes('project_id')) {
            setMapHasProjectId(false);
            var { error: retryErr } = await supabase.from('project_task_group_map')
              .upsert({ task_id: Number(taskId), group_id: Number(groupId) }, { onConflict: 'task_id' });
            if (retryErr) throw retryErr;
          } else { throw upsErr; }
        }
      }
      return { success: true };
    } catch (e) {
      if (isMissingTable(e)) { setDbAvailable(false); }
      dbgErr('moveTaskToGroup error', e);
      return { success: false, error: e.message };
    }
  }, [dbAvailable, mapHasProjectId, projectId, setTaskGroupMap, setDbAvailable]);

  // ── Reorder groups ─────────────────────────────────────────────────────
  var reorderGroups = useCallback(async function (reorderedGroups) {
    var updated = reorderedGroups.map(function (g, i) { return { ...g, order: i }; });
    setGroups(function () { return updated; });
    sendBroadcast(updated, mapRef.current);
    if (dbAvailable === false) return { success: true };
    try {
      await requireSession();
      await Promise.all(updated
        .filter(function (g) { return !String(g.id).startsWith('grp_'); })
        .map(function (g) {
          return supabase.from('project_task_groups')
            .update({ position: g.order, updated_at: new Date().toISOString() }).eq('id', g.id);
        }));
      return { success: true };
    } catch (e) { dbgErr('reorderGroups error', e); return { success: false, error: e.message }; }
  }, [dbAvailable, setGroups]);

  return {
    groups,
    taskGroupMap,
    loading,
    dbAvailable,
    setGroups,
    setTaskGroupMap,
    createGroup,
    renameGroup,
    deleteGroup,
    moveTaskToGroup,
    reorderGroups,
    reload: load,
  };
}
